// services/googleDocsService.js - Google Docs API integration for frontend
import axios from 'axios';

/**
 * GoogleDocsService - Handles Google Docs operations from the frontend
 * Uses user's Google account authentication (OAuth)
 */
class GoogleDocsService {
  constructor(accessToken) {
    this.accessToken = accessToken;
    this.baseUrl = 'https://www.googleapis.com';
  }

  /**
   * Create a new Google Doc from HTML content
   * @param {string} title - Document title
   * @param {string} htmlContent - HTML content to insert
   * @param {string} folderId - Optional Google Drive folder ID
   * @returns {Promise<Object>} { documentId, documentUrl }
   */
  async createDocument(title, htmlContent = '', folderId = null) {
    try {
      // Step 1: Create blank Google Doc
      const metadata = {
        name: title,
        mimeType: 'application/vnd.google-apps.document',
        ...(folderId && { parents: [folderId] })
      };

      const createResponse = await axios.post(
        `${this.baseUrl}/drive/v3/files`,
        metadata,
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          },
          params: {
            fields: 'id,name,webViewLink'
          }
        }
      );

      const documentId = createResponse.data.id;

      // Step 2: If htmlContent provided, insert it
      if (htmlContent && htmlContent.trim()) {
        await this.insertHtmlContent(documentId, htmlContent);
      }

      return {
        documentId: documentId,
        documentUrl: `https://docs.google.com/document/d/${documentId}/edit`,
        viewUrl: createResponse.data.webViewLink
      };
    } catch (error) {
      console.error('Error creating Google Doc:', error);
      throw new Error(`Failed to create document: ${error.message}`);
    }
  }

  /**
   * Insert HTML content into a Google Doc
   * @param {string} documentId - Google Doc ID
   * @param {string} htmlContent - HTML content to insert
   */
  async insertHtmlContent(documentId, htmlContent) {
    try {
      // Convert HTML to plain text (Google Docs API doesn't directly accept HTML)
      // For better formatting, you'd need to parse HTML and create structured requests
      const plainText = this.htmlToPlainText(htmlContent);

      const requests = [
        {
          insertText: {
            location: { index: 1 },
            text: plainText
          }
        }
      ];

      await axios.post(
        `${this.baseUrl}/docs/v1/documents/${documentId}:batchUpdate`,
        { requests },
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (error) {
      console.error('Error inserting content:', error);
      throw new Error(`Failed to insert content: ${error.message}`);
    }
  }

  /**
   * Import DOCX file to Google Docs
   * @param {File} file - DOCX file to upload
   * @param {string} title - Document title
   * @returns {Promise<Object>} { documentId, documentUrl }
   */
  async importDocx(file, title) {
    try {
      console.log('📤 Starting DOCX import to Google Drive...');
      console.log('File details:', {
        name: file.name,
        size: file.size,
        type: file.type
      });

      const metadata = {
        name: title || file.name.replace('.docx', ''),
        mimeType: 'application/vnd.google-apps.document'
      };

      console.log('Metadata:', metadata);

      // Step 1: Upload raw DOCX file first (not converted)
      console.log('Step 1: Uploading raw DOCX file to Google Drive...');

      const uploadResponse = await axios.post(
        `${this.baseUrl}/upload/drive/v3/files?uploadType=media`,
        file,
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            'Content-Type': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          }
        }
      );

      console.log('✅ Raw DOCX uploaded, file ID:', uploadResponse.data.id);

      // Step 2: Copy the file and convert it to Google Docs format
      console.log('Step 2: Converting to Google Docs format...');

      const copyResponse = await axios.post(
        `${this.baseUrl}/drive/v3/files/${uploadResponse.data.id}/copy`,
        {
          name: metadata.name,
          mimeType: 'application/vnd.google-apps.document'
        },
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          },
          params: {
            fields: 'id,name,webViewLink'
          }
        }
      );

      console.log('✅ Converted to Google Docs:', copyResponse.data);

      // Step 3: Delete the original DOCX file (optional cleanup)
      try {
        await axios.delete(
          `${this.baseUrl}/drive/v3/files/${uploadResponse.data.id}`,
          {
            headers: {
              Authorization: `Bearer ${this.accessToken}`
            }
          }
        );
        console.log('✅ Cleaned up raw DOCX file');
      } catch (deleteError) {
        console.warn('Could not delete original file:', deleteError.message);
      }

      const response = copyResponse;

      console.log('✅ Google Drive response:', response.data);

      const documentId = response.data.id;

      return {
        documentId: documentId,
        documentUrl: `https://docs.google.com/document/d/${documentId}/edit`,
        viewUrl: response.data.webViewLink
      };
    } catch (error) {
      console.error('❌ Error importing DOCX to Google Drive:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error headers:', error.response?.headers);
      throw new Error(`Failed to import DOCX: ${error.response?.data?.error?.message || error.message}`);
    }
  }

  /**
   * Export Google Doc to DOCX
   * @param {string} documentId - Google Doc ID
   * @param {string} fileName - Output filename
   * @returns {Promise<Blob>} DOCX file blob
   */
  async exportToDocx(documentId, fileName = 'document.docx') {
    try {
      const response = await axios.get(
        `${this.baseUrl}/drive/v3/files/${documentId}/export`,
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`
          },
          params: {
            mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          },
          responseType: 'blob'
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error exporting to DOCX:', error);
      throw new Error(`Failed to export document: ${error.message}`);
    }
  }

  /**
   * Export Google Doc to PDF
   * @param {string} documentId - Google Doc ID
   * @returns {Promise<Blob>} PDF file blob
   */
  async exportToPdf(documentId) {
    try {
      const response = await axios.get(
        `${this.baseUrl}/drive/v3/files/${documentId}/export`,
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`
          },
          params: {
            mimeType: 'application/pdf'
          },
          responseType: 'blob'
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      throw new Error(`Failed to export document: ${error.message}`);
    }
  }

  /**
   * Get document content as HTML (approximation)
   * @param {string} documentId - Google Doc ID
   * @returns {Promise<string>} HTML content
   */
  async getDocumentHtml(documentId) {
    try {
      const response = await axios.get(
        `${this.baseUrl}/drive/v3/files/${documentId}/export`,
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`
          },
          params: {
            mimeType: 'text/html'
          }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error getting document HTML:', error);
      throw new Error(`Failed to get document: ${error.message}`);
    }
  }

  /**
   * Share document with specific email or make public
   * @param {string} documentId - Google Doc ID
   * @param {string} email - Email to share with (optional, null = anyone with link)
   * @param {string} role - 'reader', 'writer', or 'commenter'
   * @param {boolean} sendNotificationEmail - Whether Google should send email notification (default: true)
   * @returns {Promise<Object>} { inviteId, permissionId, fullResponse } - inviteId allows non-Google users to access the document
   */
  async shareDocument(documentId, email = null, role = 'writer', sendNotificationEmail = true) {
    try {
      const permission = {
        type: email ? 'user' : 'anyone',
        role: role,
        ...(email && { emailAddress: email })
      };

      const params = {
        fields: '*'  // Request all available fields to see what Google returns
      };
      if (!sendNotificationEmail) {
        params.sendNotificationEmail = 'false';
      }

      const response = await axios.post(
        `${this.baseUrl}/drive/v3/files/${documentId}/permissions`,
        permission,
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          },
          params
        }
      );

      console.log('📤 Permission Created:', {
        documentId: documentId,
        email: email,
        role: role,
        permissionId: response.data.id
      });

      // Get file metadata to retrieve proper sharing link with resourceKey
      const fileMetadata = await this.getFileMetadata(documentId);

      return {
        permissionId: response.data.id,
        webViewLink: fileMetadata.webViewLink,     // Proper sharing link (includes resourceKey)
        resourceKey: fileMetadata.resourceKey,     // For reference
        sharingUrl: fileMetadata.webViewLink       // Alias for clarity
      };
    } catch (error) {
      console.error('Error sharing document:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      // Preserve the original error object so response data is accessible
      throw error;
    }
  }

  /**
   * Get file metadata including title and sharing link with resource key
   * @param {string} documentId - Google Doc ID
   * @returns {Promise<Object>} { name, webViewLink, resourceKey }
   */
  async getFileMetadata(documentId) {
    try {
      const response = await axios.get(
        `${this.baseUrl}/drive/v3/files/${documentId}`,
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          },
          params: {
            fields: 'id,name,webViewLink,resourceKey'
          }
        }
      );

      console.log('📄 File Metadata Retrieved:', {
        documentId: documentId,
        name: response.data.name,
        webViewLink: response.data.webViewLink,
        resourceKey: response.data.resourceKey
      });

      return {
        name: response.data.name,
        webViewLink: response.data.webViewLink,
        resourceKey: response.data.resourceKey
      };
    } catch (error) {
      console.error('Error getting file metadata:', error);
      throw new Error(`Failed to get file metadata: ${error.message}`);
    }
  }

  /**
   * Delete a Google Doc
   * @param {string} documentId - Google Doc ID
   * @returns {Promise<void>}
   */
  async deleteDocument(documentId) {
    try {
      await axios.delete(
        `${this.baseUrl}/drive/v3/files/${documentId}`,
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`
          }
        }
      );
    } catch (error) {
      console.error('Error deleting document:', error);
      throw new Error(`Failed to delete document: ${error.message}`);
    }
  }

  /**
   * Convert HTML to plain text (simple implementation)
   * For better formatting, consider using a library like html-to-text
   * @param {string} html - HTML content
   * @returns {string} Plain text
   */
  htmlToPlainText(html) {
    // Remove script and style tags
    let text = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    text = text.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '');

    // Replace <br> and </p> with newlines
    text = text.replace(/<br\s*\/?>/gi, '\n');
    text = text.replace(/<\/p>/gi, '\n\n');

    // Remove remaining HTML tags
    text = text.replace(/<[^>]+>/g, '');

    // Decode HTML entities
    const textArea = document.createElement('textarea');
    textArea.innerHTML = text;
    text = textArea.value;

    return text.trim();
  }

  /**
   * Create a copy of a document
   * @param {string} documentId - Source document ID
   * @param {string} newTitle - Title for the copy
   * @returns {Promise<Object>} { documentId, documentUrl }
   */
  async copyDocument(documentId, newTitle) {
    try {
      const response = await axios.post(
        `${this.baseUrl}/drive/v3/files/${documentId}/copy`,
        { name: newTitle },
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          },
          params: {
            fields: 'id,name,webViewLink'
          }
        }
      );

      const newDocumentId = response.data.id;

      return {
        documentId: newDocumentId,
        documentUrl: `https://docs.google.com/document/d/${newDocumentId}/edit`,
        viewUrl: response.data.webViewLink
      };
    } catch (error) {
      console.error('Error copying document:', error);
      throw new Error(`Failed to copy document: ${error.message}`);
    }
  }

  /**
   * Get all comments on a Google Doc
   * @param {string} documentId - Google Doc ID
   * @param {boolean} includeResolved - Include resolved comments (default: true)
   * @param {number} pageSize - Number of comments per page (default: 100, max: 100)
   * @returns {Promise<Array>} Array of comment objects
   */
  async getComments(documentId, includeResolved = true, pageSize = 100) {
    try {
      const params = {
        fields: 'comments(id,content,author,createdTime,modifiedTime,resolved,replies,quotedFileContent,anchor)',
        pageSize: Math.min(pageSize, 100)
      };

      if (!includeResolved) {
        params.includeDeleted = false;
      }

      const response = await axios.get(
        `${this.baseUrl}/drive/v3/files/${documentId}/comments`,
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`
          },
          params
        }
      );

      const comments = response.data.comments || [];

      // Filter out resolved comments if requested
      if (!includeResolved) {
        return comments.filter(comment => !comment.resolved);
      }

      return comments;
    } catch (error) {
      console.error('Error fetching comments:', error);
      throw new Error(`Failed to fetch comments: ${error.message}`);
    }
  }

  /**
   * Get a specific comment by ID
   * @param {string} documentId - Google Doc ID
   * @param {string} commentId - Comment ID
   * @returns {Promise<Object>} Comment object
   */
  async getComment(documentId, commentId) {
    try {
      const response = await axios.get(
        `${this.baseUrl}/drive/v3/files/${documentId}/comments/${commentId}`,
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`
          },
          params: {
            fields: 'id,content,author,createdTime,modifiedTime,resolved,replies,quotedFileContent,anchor'
          }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching comment:', error);
      throw new Error(`Failed to fetch comment: ${error.message}`);
    }
  }

  /**
   * Get only open (unresolved) comments on a Google Doc
   * @param {string} documentId - Google Doc ID
   * @returns {Promise<Array>} Array of unresolved comment objects
   */
  async getOpenComments(documentId) {
    return this.getComments(documentId, false);
  }

  /**
   * Reply to a comment
   * @param {string} documentId - Google Doc ID
   * @param {string} commentId - Comment ID to reply to
   * @param {string} replyContent - Reply text
   * @returns {Promise<Object>} Reply object
   */
  async replyToComment(documentId, commentId, replyContent) {
    try {
      const response = await axios.post(
        `${this.baseUrl}/drive/v3/files/${documentId}/comments/${commentId}/replies`,
        {
          content: replyContent
        },
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          },
          params: {
            fields: 'id,content,author,createdTime'
          }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error replying to comment:', error);
      throw new Error(`Failed to reply to comment: ${error.message}`);
    }
  }

  /**
   * Resolve a comment
   * @param {string} documentId - Google Doc ID
   * @param {string} commentId - Comment ID to resolve
   * @returns {Promise<Object>} Updated comment object
   */
  async resolveComment(documentId, commentId) {
    try {
      const response = await axios.patch(
        `${this.baseUrl}/drive/v3/files/${documentId}/comments/${commentId}`,
        {
          resolved: true
        },
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          },
          params: {
            fields: 'id,resolved'
          }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error resolving comment:', error);
      throw new Error(`Failed to resolve comment: ${error.message}`);
    }
  }

  /**
   * Reopen a resolved comment
   * @param {string} documentId - Google Doc ID
   * @param {string} commentId - Comment ID to reopen
   * @returns {Promise<Object>} Updated comment object
   */
  async reopenComment(documentId, commentId) {
    try {
      const response = await axios.patch(
        `${this.baseUrl}/drive/v3/files/${documentId}/comments/${commentId}`,
        {
          resolved: false
        },
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          },
          params: {
            fields: 'id,resolved'
          }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error reopening comment:', error);
      throw new Error(`Failed to reopen comment: ${error.message}`);
    }
  }
}

export default GoogleDocsService;
